import React from 'react';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPage = ({ onBack }) => {
    return (
        <div className="min-h-screen w-full bg-white font-sans text-[#1d1d1f]">
            {/* Nav */}
            <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#f5f5f7]">
                <div className="max-w-[760px] mx-auto px-6 h-14 flex items-center gap-3">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1 text-sm text-[#666] hover:text-[#1d1d1f] transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back
                    </button>
                    <div className="flex items-center gap-2 ml-auto">
                        <Shield size={18} className="text-[#FFC107]" />
                        <span className="font-semibold text-sm">Privacy Policy</span>
                    </div>
                </div>
            </nav>

            <main className="max-w-[760px] mx-auto px-6 py-12">
                <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
                <p className="text-sm text-[#999] mb-10">Last updated: February 25, 2026</p>

                <div className="space-y-10 text-[#444] text-[15px] leading-relaxed">

                    {/* Overview */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Overview</h2>
                        <p>
                            Saige ("we," "our," or "us") is a lesson planning tool designed for K–12 educators.
                            We are committed to protecting your privacy and handling your data responsibly.
                            This policy explains what data we collect, how we use it, and your rights.
                        </p>
                    </section>

                    {/* Data Collected */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Data We Collect</h2>
                        <ul className="list-disc pl-5 space-y-2">
                            <li><strong>Account Information:</strong> Name, email address, school or district name, grade level, and subjects taught.</li>
                            <li><strong>Lesson Content:</strong> Topics, objectives, and lesson plans you generate or upload.</li>
                            <li><strong>Usage Data:</strong> Feature usage patterns, lesson generation counts, and interaction timestamps.</li>
                            <li><strong>Device Information:</strong> Browser type, screen resolution, and operating system (collected automatically for performance optimization).</li>
                        </ul>
                        <p className="mt-3">
                            We do <strong>not</strong> collect student names, student data, or personally identifiable information about students.
                        </p>
                    </section>

                    {/* Authentication */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Authentication</h2>
                        <p>
                            We use <strong>Supabase Auth</strong> for secure authentication. When you sign up or log in,
                            your credentials are verified through Supabase's secure infrastructure. Authentication
                            tokens (JWT) are stored in your browser's local storage to maintain your session.
                        </p>
                    </section>

                    {/* Cookies & Local Storage */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Cookies &amp; Local Storage</h2>
                        <p>
                            Saige uses <strong>local storage</strong> to persist your session and preferences.
                            Supabase may set essential cookies for authentication purposes. These are
                            strictly functional — we do not use tracking cookies, advertising cookies,
                            or any third-party analytics cookies.
                        </p>
                        <p className="mt-2">
                            <strong>Essential cookies only:</strong> Any cookies set are required for authentication and cannot be disabled
                            without losing the ability to log in.
                        </p>
                    </section>

                    {/* Third-Party Services */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Third-Party Services</h2>
                        <p className="mb-3">We use the following third-party services to operate Saige:</p>
                        <ul className="list-disc pl-5 space-y-2">
                            <li><strong>Supabase</strong> — Database hosting and user authentication.</li>
                            <li><strong>Stripe</strong> — Payment processing for premium features. We do not store credit card numbers; all payment data is handled by Stripe.</li>
                            <li><strong>SendGrid</strong> — Transactional email delivery (account verification, password resets).</li>
                            <li><strong>Google Gemini</strong> — AI-powered lesson generation. Lesson prompts are sent to Google's API for content generation. No student data is included in these requests.</li>
                        </ul>
                    </section>

                    {/* Data Retention */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Data Retention</h2>
                        <p>
                            Your lesson data and account information are retained in your account for as long as
                            your account is active. You may request deletion of your account and associated data
                            at any time by contacting us.
                        </p>
                    </section>

                    {/* FERPA Alignment */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">FERPA Alignment</h2>
                        <p>
                            Saige is designed with <strong>FERPA-aligned principles</strong>. Student names are
                            never sent to AI models or stored in system logs. All student-facing features use
                            anonymized identifiers. We do not collect, store, or process student education records.
                        </p>
                    </section>

                    {/* Your Rights */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Your Rights</h2>
                        <ul className="list-disc pl-5 space-y-2">
                            <li>Access or export your account data at any time.</li>
                            <li>Request correction of inaccurate information.</li>
                            <li>Request deletion of your account and all associated data.</li>
                            <li>Opt out of non-essential communications.</li>
                        </ul>
                    </section>

                    {/* Contact */}
                    <section>
                        <h2 className="text-lg font-semibold text-[#1d1d1f] mb-3">Contact Us</h2>
                        <p>
                            If you have questions about this privacy policy or your data, please contact us at{' '}
                            <a href="mailto:support@humaniqclass.com" className="text-[#FFC107] hover:underline font-medium">
                                support@humaniqclass.com
                            </a>.
                        </p>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 text-center text-[#ccc] text-xs tracking-wide border-t border-[#f5f5f7] mt-12">
                <p>&copy; 2026 Saige</p>
            </footer>
        </div>
    );
};

export default PrivacyPage;
